import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  Experience,
  ExperiencesCollection,
} from '@freelancer/datastore/collections';
import { DurationFormat } from '@freelancer/pipes';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { trackById } from '@freelancer/ui/helpers';
import { ListItemPadding, ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import {
  FontColor,
  FontStyle,
  FontType,
  FontWeight,
  TextSize,
} from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import { UserProfileViewState } from '../../user-profile-editable-ui/user-profile-editable-ui.model';

@Component({
  selector: 'app-user-profile-experiences',
  template: `
    <ng-container *ngIf="!(experiencesCollection.status$ | flAsync)?.error">
      <ng-container *ngIf="experiences$ | flAsync as experiences">
        <fl-card
          *ngIf="
            (experiences && experiences.length > 0) ||
            (isViewerProfileOwner$ | flAsync)
          "
        >
          <fl-card-header-title i18n="Experience card title">
            Experience
          </fl-card-header-title>

          <fl-card-header-right
            *ngIf="
              (currentViewState$ | flAsync) === UserProfileViewState.VIEW &&
              (isViewerProfileOwner$ | flAsync)
            "
          >
            <fl-button
              i18n="Add experience button"
              flTrackingLabel="UserProfileAddExperience"
              [color]="ButtonColor.SECONDARY"
              (click)="handleAddExperience()"
            >
              Add Experience
            </fl-button>
          </fl-card-header-right>

          <ng-container
            *ngIf="(currentViewState$ | flAsync) === UserProfileViewState.VIEW"
          >
            <ng-container *ngIf="experiences?.length > 0; else emptyState">
              <fl-list
                [outerPadding]="false"
                [padding]="ListItemPadding.SMALL"
                [flMarginBottom]="
                  displayAmount < experiences.length ? Margin.MID : Margin.NONE
                "
              >
                <fl-list-item
                  *ngFor="
                    let experience of experiences | slice: 0:displayAmount;
                    trackBy: trackById
                  "
                >
                  <app-user-profile-experiences-view
                    [experience]="experience"
                    [experiencesCollection]="experiencesCollection"
                    [isViewerProfileOwner$]="isViewerProfileOwner$"
                    (edit)="handleExperienceEditAction($event)"
                  ></app-user-profile-experiences-view>
                </fl-list-item>
              </fl-list>
              <fl-bit
                *ngIf="displayAmount < experiences.length"
                class="UserProfileExperience-footer"
              >
                <fl-button
                  i18n="Experience show more button"
                  flTrackingLabel="ExperienceLoadMoreButton"
                  [color]="ButtonColor.DEFAULT"
                  (click)="showMore()"
                >
                  Load More
                </fl-button>
              </fl-bit>
            </ng-container>
            <ng-template #emptyState>
              <fl-text
                i18n="Empty education message"
                [size]="TextSize.SMALL"
                [style]="FontStyle.ITALIC"
              >
                No experiences have been added.
              </fl-text>
            </ng-template>
          </ng-container>

          <ng-container
            *ngIf="(currentViewState$ | flAsync) === UserProfileViewState.EDIT"
          >
            <app-user-profile-experiences-edit
              [experiencesCollection]="experiencesCollection"
              [experience$]="currentEditingExperience$"
              (cancel)="handleExperienceEditCancel()"
            ></app-user-profile-experiences-edit>
          </ng-container>
        </fl-card>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./user-profile-experiences.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileExperiencesComponent implements OnInit {
  ListItemPadding = ListItemPadding;
  ListItemType = ListItemType;
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  TextSize = TextSize;
  FontStyle = FontStyle;
  Margin = Margin;
  HeadingType = HeadingType;
  DurationFormat = DurationFormat;
  ButtonColor = ButtonColor;
  UserProfileViewState = UserProfileViewState;
  trackById = trackById;

  @Input() experiencesCollection: DatastoreCollection<ExperiencesCollection>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;

  readonly INITIAL_DISPLAY_AMOUNT = 3;
  displayAmount = this.INITIAL_DISPLAY_AMOUNT;

  experiences$: Rx.Observable<readonly Experience[]>;

  private _currentViewState$ = new Rx.BehaviorSubject<UserProfileViewState>(
    UserProfileViewState.VIEW,
  );
  currentViewState$ = this._currentViewState$.asObservable();

  private _currentEditingExperience$ = new Rx.BehaviorSubject<
    Experience | undefined
  >(undefined);
  currentEditingExperience$ = this._currentEditingExperience$.asObservable();

  constructor(private timeUtils: TimeUtils) {}

  ngOnInit(): void {
    this.experiences$ = this.experiencesCollection.valueChanges().pipe(
      map(experiences =>
        experiences.map(experience => {
          if (experience.duration) {
            const duration = this.timeUtils.roundDuration(experience.duration);
            return { ...experience, duration };
          }
          return experience;
        }),
      ),
    );
  }

  showMore(): void {
    this.displayAmount += 3;
  }

  handleAddExperience(): void {
    this.clearCurrentEditingExperience();
    this.switchToExperienceEditState();
  }

  handleExperienceEditAction(experience: Experience): void {
    this._currentEditingExperience$.next(experience);
    this.switchToExperienceEditState();
  }

  handleExperienceEditCancel(): void {
    this.clearCurrentEditingExperience();
    this.switchToExperienceViewState();
  }

  clearCurrentEditingExperience(): void {
    this._currentEditingExperience$.next(undefined);
  }

  switchToExperienceEditState(): void {
    this._currentViewState$.next(UserProfileViewState.EDIT);
  }

  switchToExperienceViewState(): void {
    this._currentViewState$.next(UserProfileViewState.VIEW);
  }
}
